"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import Image from "next/image"
import { m } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"

export type TimelineStep = {
  titleKey: string
  descKey: string
  /**
   * Optional prerendered description, overriding `t(descKey)`. Use when the copy
   * carries a runtime value (e.g. a live CMS count) so the caller can interpolate
   * it without an ICU placeholder leaking into the shared message namespace.
   */
  desc?: ReactNode
  /** Optional per-step photo (Blob URL). Falls back to a numbered placeholder. */
  image?: string
}

export type ImmuneStepsTimelineProps = {
  /** Optional uppercase eyebrow above the heading. */
  label?: string
  headingKey: string
  bodyKey?: string
  /** Word shown in the active-step badge, e.g. "Step". */
  stepWord?: string
  steps: readonly TimelineStep[]
  namespace?: string
  /** Optional CTA rendered inside the white card, below the steps. */
  cta?: ReactNode
}

const EXPO = [0.16, 1, 0.3, 1] as const

/** Per-step media panel — a photo when provided, else a numbered placeholder. */
function StepVisual({ index, src }: { index: number; src?: string }) {
  if (src) {
    return (
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px]">
        <Image
          src={src}
          alt=""
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          className="object-cover"
        />
      </div>
    )
  }
  return (
    <div className="from-deep-green-50 relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-[16px] bg-gradient-to-br to-neutral-100">
      <span className="type-display-lg text-deep-green-700/30 leading-none select-none">
        {String(index + 1).padStart(2, "0")}
      </span>
    </div>
  )
}

/**
 * "How it works" — a clickable, scroll-linked connected stepper. Numbered
 * nodes sit on a connector line that fills to the active step; a detail panel
 * (media + "Step 0X" badge + title + body) swaps as the active step changes.
 * Desktop: a tall sticky section advances the active step on scroll; clicking
 * a node jumps the page to that step. Mobile: a plain stacked list.
 *
 * Concept adapted from Tristan's `VisitWalkthroughTimeline` (full-body MRI
 * redesign, PR #1309).
 */
export function ImmuneStepsTimeline({
  label,
  headingKey,
  bodyKey,
  stepWord = "Step",
  steps,
  namespace,
  cta,
}: ImmuneStepsTimelineProps) {
  // Cast: next-intl strictly types the namespace; we accept an arbitrary one
  // so the component composes across namespaces.
  const t = useTranslations(namespace as Parameters<typeof useTranslations>[0])
  const TOTAL = steps.length
  const [activeStep, setActiveStep] = useState(0)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Scroll progress through the tall section drives the active step.
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    let raf = 0
    const compute = () => {
      raf = 0
      const rect = section.getBoundingClientRect()
      const scrollRange = section.offsetHeight - window.innerHeight
      if (scrollRange <= 0) return
      const p = Math.max(0, Math.min(1, -rect.top / scrollRange))
      setActiveStep(Math.min(TOTAL - 1, Math.floor(p * TOTAL)))
    }
    const onScroll = () => {
      if (raf === 0) raf = requestAnimationFrame(compute)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    compute()
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [TOTAL])

  // Click a node → scroll the page to the middle of that step's zone.
  const jumpTo = (i: number) => {
    const section = sectionRef.current
    if (!section) return
    const scrollRange = section.offsetHeight - window.innerHeight
    const sectionTop = section.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top: sectionTop + ((i + 0.5) / TOTAL) * scrollRange,
      behavior: "smooth",
    })
  }

  const step = steps[activeStep]
  // Fill snaps to the active node instead of tracking raw scroll progress, so
  // the bar clicks cleanly into the next step rather than creeping (and
  // stalling) across the gap between nodes.
  const fillPct = TOTAL > 1 ? (activeStep / (TOTAL - 1)) * 100 : 100

  return (
    <div className="bg-background py-3 md:py-4">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Mobile: stacked list */}
        <section className="py-14 md:hidden">
          <div className="mx-auto w-full px-6 text-center">
            {label && (
              <p className="type-label-md mb-3 text-neutral-600 uppercase">
                {label}
              </p>
            )}
            <h2 className="type-display-md text-neutral-900">
              {t(headingKey)}
            </h2>
            {bodyKey && (
              <p className="type-body-md mt-3 text-neutral-600">{t(bodyKey)}</p>
            )}
          </div>
          <div className="mt-10 flex flex-col gap-12 px-6">
            {steps.map((s, i) => (
              <div key={s.titleKey}>
                <StepVisual index={i} src={s.image} />
                <h3 className="type-title-md mt-4 text-neutral-900">
                  {t(s.titleKey)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">
                  {s.desc ?? t(s.descKey)}
                </p>
              </div>
            ))}
          </div>
          {cta && <div className="mt-10 flex justify-center px-6">{cta}</div>}
        </section>

        {/* Desktop: sticky scroll-linked clickable stepper */}
        <section
          ref={sectionRef}
          className="relative hidden md:block"
          style={{ minHeight: `${TOTAL * 100}vh` }}
        >
          <div className="sticky top-0 flex h-screen flex-col justify-center py-16">
            <div className="mx-auto mb-12 w-full max-w-[640px] px-4 text-center">
              {label && (
                <p className="type-label-md mb-3 text-neutral-600 uppercase">
                  {label}
                </p>
              )}
              <h2 className="type-display-md text-neutral-900">
                {t(headingKey)}
              </h2>
              {bodyKey && (
                <p className="type-body-lg mt-3 text-neutral-600">
                  {t(bodyKey)}
                </p>
              )}
            </div>

            <div className="mx-auto w-full max-w-[1100px] px-4 md:px-10">
              {/* Stepper rail */}
              <div className="relative mx-[12%]">
                <div className="absolute top-1/2 right-0 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-200" />
                <div
                  className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-900 transition-[width] duration-300"
                  style={{
                    // Stop the fill short of the target node so there's a small
                    // gap before the circle (line · gap · circle).
                    width: fillPct > 0 ? `calc(${fillPct}% - 14px)` : "0px",
                  }}
                />
                <div className="relative flex justify-between">
                  {steps.map((s, i) => {
                    const done = i <= activeStep
                    return (
                      <button
                        key={s.titleKey}
                        type="button"
                        aria-current={i === activeStep}
                        aria-label={t(s.titleKey)}
                        onClick={() => jumpTo(i)}
                        className={cn(
                          "type-title-sm flex h-9 w-9 cursor-pointer items-center justify-center rounded-full font-medium transition-[transform,background-color,color] duration-300",
                          done
                            ? "bg-neutral-900 text-white"
                            : "bg-white text-neutral-900 shadow-[inset_0_0_0_2px_rgba(0,0,0,0.2)]",
                          i === activeStep && "scale-110"
                        )}
                      >
                        {i + 1}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Detail panel — the image layer is persistent (all steps
                  mounted + cross-faded) so photos preload and switch with no
                  fetch delay; only the text swaps with a fade. */}
              <div className="mt-12 grid items-center gap-10 md:grid-cols-2 md:gap-16">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px]">
                  {steps.map((s, i) => (
                    <div
                      key={s.titleKey}
                      aria-hidden={i !== activeStep}
                      className="absolute inset-0 transition-opacity duration-500 ease-out"
                      style={{ opacity: i === activeStep ? 1 : 0 }}
                    >
                      {s.image ? (
                        <Image
                          src={s.image}
                          alt=""
                          fill
                          sizes="(max-width: 768px) 100vw, 50vw"
                          className="object-cover"
                        />
                      ) : (
                        <div className="from-deep-green-50 flex h-full w-full items-center justify-center bg-gradient-to-br to-neutral-100">
                          <span className="type-display-lg text-deep-green-700/30 leading-none select-none">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <m.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    ease: EXPO as unknown as number[],
                  }}
                >
                  <p className="type-label-sm bg-deep-green-700 inline-flex items-center rounded-full px-3 py-1 text-white uppercase">
                    {stepWord} {String(activeStep + 1).padStart(2, "0")}
                  </p>
                  <h3 className="type-display-sm mt-3 text-neutral-900">
                    {t(step.titleKey)}
                  </h3>
                  <p className="type-body-lg mt-3 text-neutral-600">
                    {step.desc ?? t(step.descKey)}
                  </p>
                </m.div>
              </div>

              {cta && <div className="mt-10 flex justify-center">{cta}</div>}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
